import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowRight, MapPin } from 'lucide-react';

import Modal from '../common/Modal';
import { useToast } from '../../contexts/ToastContext';
import { formatDateTime } from '../../utils/dateTime';
import { dispatchService } from '../../services/dispatchService';

interface PastDispatchUpdateModalsProps {
  updateId: number;
  action: 'view' | 'approve' | 'reject';
  onClose: () => void;
}

const ITEM_FIELDS: { key: string; label: string }[] = [
  { key: 'goodQty', label: 'Good Qty' },
  { key: 'cdamQty', label: 'Cosmetic Damage' },
  { key: 'damQty', label: 'Damage' },
  { key: 'palletCount', label: 'Pallets' },
  { key: 'batchNo', label: 'Batch No' },
  { key: 'remarks', label: 'Remarks' },
];

const HEADER_FIELDS: { key: string; label: string }[] = [
  { key: 'destination', label: 'Destination' },
  { key: 'lrNumber', label: 'LR Number' },
  { key: 'eWaybillNumber', label: 'E-Waybill' },
  { key: 'vehicleNo', label: 'Vehicle No' },
  { key: 'driverName', label: 'Driver' },
  { key: 'driverPhone', label: 'Driver Phone' },
  { key: 'transporterName', label: 'Transporter' },
  { key: 'lrDate', label: 'LR Date' },
  { key: 'handlingType', label: 'Handling Type' },
  { key: 'remarks', label: 'Remarks' },
];

// Human-readable current-location chips from an enhanced dispatch item's
// resolved storage selections (each carries zone/rack/location codes captured
// at dispatch time).
const locationChips = (item: any): string[] => {
  if (!item) return [];
  const selections = Array.isArray(item.storageSelections)
    ? item.storageSelections
    : [];
  const chips: string[] = [];
  for (const s of selections) {
    if (!s || typeof s !== 'object') continue;
    const rack = s.rack || null;
    const loc = s.location || null;
    // Prefer the specific zone-location slot code (e.g. "ZONEALBD-01") over
    // the bare zone code — otherwise every pallet picked from the same zone
    // collapses into one identical, indistinguishable chip.
    const zone = s.zoneLocation || s.zone || null;
    const label = [zone, rack, loc].filter(Boolean).join(' / ');
    const pallets = s.palletsToDispatch ? ` · ${s.palletsToDispatch} plt` : '';
    if (label) chips.push(`${label}${pallets}`);
  }
  return chips;
};

const PastDispatchUpdateModals: React.FC<PastDispatchUpdateModalsProps> = ({
  updateId,
  action,
  onClose,
}) => {
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [rejectionReason, setRejectionReason] = useState('');

  const { data: detail, isLoading } = useQuery({
    queryKey: ['past-dispatch-update', updateId],
    queryFn: () => dispatchService.getPastDispatchUpdate(updateId),
    enabled: action === 'view',
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['past-dispatch-updates'] });
    queryClient.invalidateQueries({ queryKey: ['inventory'] });
    // Reflect the applied edit immediately in the Outward report and dispatch lists.
    queryClient.invalidateQueries({ queryKey: ['reports'] });
    queryClient.invalidateQueries({ queryKey: ['dispatches'] });
    queryClient.invalidateQueries({ queryKey: ['gateEntries'] });
  };

  const approveMutation = useMutation({
    mutationFn: () => dispatchService.approvePastDispatchUpdate(updateId),
    onSuccess: () => {
      showToast('Past dispatch update approved and applied', 'success');
      invalidate();
      onClose();
    },
    onError: (error: any) =>
      showToast(error?.message || 'Failed to approve update', 'error'),
  });

  const rejectMutation = useMutation({
    mutationFn: () =>
      dispatchService.rejectPastDispatchUpdate(updateId, rejectionReason.trim()),
    onSuccess: () => {
      showToast('Past dispatch update rejected', 'success');
      invalidate();
      onClose();
    },
    onError: (error: any) =>
      showToast(error?.message || 'Failed to reject update', 'error'),
  });

  const isMutating = approveMutation.isPending || rejectMutation.isPending;

  if (action === 'view') {
    const originalItems: any[] = detail?.originalSnapshot?.items ?? [];
    const proposedItems: any[] = detail?.proposedItems ?? [];
    const proposedById = new Map(
      proposedItems.map((item) => [item.dispatchItemId, item]),
    );
    const currentItems: any[] = detail?.currentItems ?? [];
    const itemsById = new Map(currentItems.map((it: any) => [it.id, it]));

    const originalHeader: any = detail?.originalSnapshot?.header ?? {};
    const proposedHeader: any = detail?.proposedHeader ?? null;
    const headerRows = proposedHeader
      ? HEADER_FIELDS.map((f) => {
          const before = originalHeader[f.key];
          const after =
            proposedHeader[f.key] !== undefined ? proposedHeader[f.key] : before;
          return {
            ...f,
            before,
            after,
            changed: String(before ?? '') !== String(after ?? ''),
          };
        }).filter((r) => r.changed)
      : [];

    return (
      <Modal
        isOpen
        onClose={onClose}
        title={`Past Dispatch Update — ${detail?.dispatchNumber || ''}`}
        size="xl"
      >
        {isLoading ? (
          <div className="py-12 text-center text-gray-400 text-sm">Loading…</div>
        ) : (
          <div className="space-y-5 max-h-[70vh] overflow-y-auto pr-1">
            {/* Meta bar */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
              <div className="sm:col-span-3">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Reason for edit</div>
                <div className="text-sm text-gray-900 mt-0.5">{detail?.editReason || '—'}</div>
              </div>
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Requested by</div>
                <div className="text-sm text-gray-900 mt-0.5">{detail?.requestedByName || '—'}</div>
              </div>
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Requested on</div>
                <div className="text-sm text-gray-900 mt-0.5">
                  {detail?.requestedAt ? formatDateTime(detail.requestedAt) : '—'}
                </div>
              </div>
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Dispatch</div>
                <div className="text-sm font-medium text-gray-900 mt-0.5">{detail?.dispatchNumber || '—'}</div>
              </div>
            </div>

            {/* Header (logistics) changes */}
            {headerRows.length > 0 && (
              <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
                <div className="px-4 py-3 bg-gray-50 border-b border-gray-100">
                  <h4 className="text-sm font-semibold text-gray-900">Logistics / paperwork changes</h4>
                </div>
                <div className="divide-y divide-gray-50">
                  {headerRows.map((r) => (
                    <div
                      key={r.key}
                      className="grid grid-cols-[150px_1fr_28px_1fr] items-center gap-2 px-4 py-2 text-sm bg-amber-50/60"
                    >
                      <span className="text-gray-500">{r.label}</span>
                      <span className="truncate text-gray-500 line-through">
                        {String(r.before ?? '') === '' ? '—' : String(r.before)}
                      </span>
                      <span className="flex justify-center">
                        <ArrowRight className="w-4 h-4 text-amber-500" />
                      </span>
                      <span className="truncate font-medium text-amber-700">
                        {String(r.after ?? '') === '' ? '—' : String(r.after)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Per-item comparison cards */}
            {originalItems.map((orig) => {
              const proposed = proposedById.get(orig.dispatchItemId) || {};
              const rows = ITEM_FIELDS.map((f) => {
                const before = orig[f.key];
                const after = proposed[f.key] !== undefined ? proposed[f.key] : before;
                return {
                  ...f,
                  before,
                  after,
                  changed: String(before ?? '') !== String(after ?? ''),
                };
              });
              const changedCount = rows.filter((r) => r.changed).length;
              const chips = locationChips(itemsById.get(orig.dispatchItemId));

              return (
                <div
                  key={orig.dispatchItemId}
                  className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden"
                >
                  {/* Card header */}
                  <div className="flex items-center justify-between gap-3 px-4 py-3 bg-gray-50 border-b border-gray-100">
                    <h4 className="text-sm font-semibold text-gray-900 truncate" title={orig.productDescription}>
                      {orig.productDescription}
                    </h4>
                    {changedCount > 0 ? (
                      <span className="shrink-0 px-2 py-0.5 text-[11px] font-semibold rounded-full bg-amber-100 text-amber-700 border border-amber-200">
                        {changedCount} change{changedCount !== 1 ? 's' : ''}
                      </span>
                    ) : (
                      <span className="shrink-0 px-2 py-0.5 text-[11px] font-semibold rounded-full bg-gray-100 text-gray-500 border border-gray-200">
                        No changes
                      </span>
                    )}
                  </div>

                  {/* Picked-from location */}
                  <div className="px-4 py-2.5 border-b border-gray-100 flex items-center gap-2 flex-wrap">
                    <span className="flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                      <MapPin className="w-3.5 h-3.5" /> Picked from
                    </span>
                    {chips.length > 0 ? (
                      chips.map((chip, i) => (
                        <span
                          key={i}
                          className="px-2 py-0.5 text-xs font-medium rounded-md bg-gray-100 text-gray-700 border border-gray-200"
                        >
                          {chip}
                        </span>
                      ))
                    ) : (
                      <span className="text-xs text-gray-400 italic">Not recorded</span>
                    )}
                  </div>

                  {/* Field comparison */}
                  <div className="divide-y divide-gray-50">
                    {rows.map((r) => (
                      <div
                        key={r.key}
                        className={`grid grid-cols-[150px_1fr_28px_1fr] items-center gap-2 px-4 py-2 text-sm ${
                          r.changed ? 'bg-amber-50/60' : ''
                        }`}
                      >
                        <span className="text-gray-500">{r.label}</span>
                        <span className={`truncate ${r.changed ? 'text-gray-500 line-through' : 'text-gray-800'}`}>
                          {String(r.before ?? '') === '' ? '—' : String(r.before)}
                        </span>
                        <span className="flex justify-center">
                          {r.changed && <ArrowRight className="w-4 h-4 text-amber-500" />}
                        </span>
                        <span className={`truncate font-medium ${r.changed ? 'text-amber-700' : 'text-gray-800'}`}>
                          {String(r.after ?? '') === '' ? '—' : String(r.after)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Modal>
    );
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={action === 'approve' ? 'Approve Past Dispatch Update' : 'Reject Past Dispatch Update'}
      size="md"
      footer={
        <div className="flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={() => {
              if (action === 'approve') {
                approveMutation.mutate();
              } else {
                if (!rejectionReason.trim()) {
                  showToast('Please provide a rejection reason', 'warning');
                  return;
                }
                rejectMutation.mutate();
              }
            }}
            disabled={isMutating}
            className={`px-4 py-2 text-sm font-medium text-white rounded-lg disabled:bg-gray-300 ${
              action === 'approve'
                ? 'bg-green-600 hover:bg-green-700'
                : 'bg-red-600 hover:bg-red-700'
            }`}
          >
            {isMutating
              ? 'Processing…'
              : action === 'approve'
                ? 'Confirm Approval'
                : 'Confirm Rejection'}
          </button>
        </div>
      }
    >
      {action === 'approve' ? (
        <p className="text-sm text-gray-600">
          Approving will apply the edited values to the dispatch and re-sync
          inventory and client stock (outward quantities are added back or
          re-removed by the net change). This cannot be undone.
        </p>
      ) : (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Rejection reason <span className="text-red-500">*</span>
          </label>
          <textarea
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            rows={3}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-brand-500"
            placeholder="Explain why this update is being rejected"
          />
        </div>
      )}
    </Modal>
  );
};

export default PastDispatchUpdateModals;
